import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, CalendarPlus, CheckCircle2, Edit, List, Search, Users, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  cancelAppointment,
  createAppointment,
  listAppointments,
  markAppointmentCompleted,
  updateAppointment
} from '../api/appointments';
import { listCustomers } from '../api/customers';
import { listPets } from '../api/pets';
import { listProfessionals } from '../api/professionals';
import { getErrorMessage } from '../api/response';
import { listServices } from '../api/services';
import { getBranches } from '../api/tenant';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import FormField, { inputClassName } from '../components/FormField';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import { appointmentStatus, getStatusConfig } from '../utils/status';
import { dateTime, fromInputDateTime, toInputDateTime } from '../utils/format';

const emptyForm = {
  customer_id: '',
  pet_id: '',
  service_id: '',
  professional_id: '',
  branch_id: '',
  starts_at: '',
  status: 'scheduled',
  notes: ''
};

const viewOptions = [
  { value: 'day', label: 'Dia' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' }
];

const displayOptions = [
  { value: 'calendar', label: 'Calendario', icon: CalendarDays },
  { value: 'list', label: 'Listado', icon: List }
];

const statusOptions = [
  { value: '', label: 'Todos' },
  { value: 'scheduled', label: 'Agendada' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'confirmed', label: 'Confirmada' },
  { value: 'completed', label: 'Atendida' },
  { value: 'cancelled', label: 'Cancelada' },
  { value: 'no_show', label: 'No asistio' }
];

const isoDate = (date) => date.toISOString().slice(0, 10);

const shortDate = (value) => {
  const date = new Date(`${value}T00:00:00`);
  return new Intl.DateTimeFormat('es-CL', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit'
  }).format(date);
};

const rangeForView = (dateValue, view) => {
  const base = new Date(`${dateValue}T00:00:00`);
  const start = new Date(base);
  const end = new Date(base);

  if (view === 'week') {
    const day = start.getDay() || 7;
    start.setDate(start.getDate() - day + 1);
    end.setTime(start.getTime());
    end.setDate(start.getDate() + 6);
  }

  if (view === 'month') {
    start.setDate(1);
    end.setMonth(start.getMonth() + 1, 0);
  }

  return {
    from: `${isoDate(start)} 00:00:00`,
    to: `${isoDate(end)} 23:59:59`
  };
};

const datesForView = (dateValue, view) => {
  const range = rangeForView(dateValue, view);
  const start = new Date(`${range.from.slice(0, 10)}T00:00:00`);
  const end = new Date(`${range.to.slice(0, 10)}T00:00:00`);
  const dates = [];

  for (const current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
    dates.push(isoDate(current));
  }

  return dates;
};

const minutesToTime = (minutes) => {
  const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mins = String(minutes % 60).padStart(2, '0');
  return `${hours}:${mins}`;
};

const appointmentDate = (appointment) => String(appointment.starts_at || '').replace('T', ' ').slice(0, 10);
const appointmentTime = (appointment) => String(appointment.starts_at || '').replace('T', ' ').slice(11, 16);
const appointmentSlotKey = (appointment) => `${appointmentDate(appointment)} ${appointmentTime(appointment)}`;

const AppointmentCard = ({ appointment, onEdit }) => {
  const config = getStatusConfig(appointmentStatus, appointment.status);

  return (
    <button
      type="button"
      onClick={() => onEdit(appointment)}
      className="mb-2 w-full rounded-md border border-blue-200 bg-blue-50 p-2 text-left shadow-sm transition hover:border-blue-300 hover:bg-blue-100"
      title="Editar cita"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 truncate text-xs font-semibold text-blue-950">
          {appointment.pet_name || `Mascota #${appointment.pet_id}`}
        </span>
        <StatusBadge label={config.label} color={config.color} />
      </div>
      <p className="mt-1 truncate text-xs text-blue-900">{appointment.customer_name || `Cliente #${appointment.customer_id}`}</p>
      <p className="mt-1 truncate text-[11px] text-blue-700">{appointment.service_name || `Servicio #${appointment.service_id}`}</p>
    </button>
  );
};

const CalendarBoard = ({
  appointments,
  columns,
  dateValue,
  view,
  onCreate,
  onEdit
}) => {
  const rows = useMemo(() => {
    const dates = datesForView(dateValue, view);
    const baseTimes = Array.from({ length: 22 }).map((_, index) => minutesToTime(8 * 60 + index * 30));
    const appointmentTimes = appointments.map(appointmentTime).filter(Boolean);
    const times = view === 'month'
      ? Array.from(new Set(appointmentTimes)).sort()
      : Array.from(new Set([...baseTimes, ...appointmentTimes])).sort();

    if (times.length === 0) {
      return [];
    }

    return dates.flatMap((day) => times.map((slotTime) => ({
      date: day,
      time: slotTime,
      key: `${day} ${slotTime}`
    })));
  }, [appointments, dateValue, view]);

  const appointmentsByCell = useMemo(() => {
    const grouped = new Map();

    appointments.forEach((appointment) => {
      const professionalKey = appointment.professional_id ? String(appointment.professional_id) : 'unassigned';
      const key = `${professionalKey}|${appointmentSlotKey(appointment)}`;
      const current = grouped.get(key) || [];
      current.push(appointment);
      grouped.set(key, current);
    });

    return grouped;
  }, [appointments]);

  if (columns.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-gray-600">
        Selecciona al menos un veterinario para ver el calendario.
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-gray-600">
        No hay horarios para mostrar en este rango.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div
        className="min-w-[920px]"
        style={{ minWidth: Math.max(920, 104 + columns.length * 220) }}
      >
        <div
          className="grid border-b border-gray-200 bg-gray-50"
          style={{ gridTemplateColumns: `104px repeat(${columns.length}, minmax(200px, 1fr))` }}
        >
          <div className="sticky left-0 z-10 border-r border-gray-200 bg-gray-50 px-3 py-3 text-xs font-semibold uppercase text-gray-500">
            Hora
          </div>
          {columns.map((professional) => (
            <div key={professional.id} className="border-r border-gray-200 px-3 py-3">
              <p className="truncate text-sm font-semibold text-gray-950">{professional.full_name}</p>
              <p className="truncate text-xs text-gray-500">{professional.specialty || professional.roleLabel || 'Veterinario'}</p>
            </div>
          ))}
        </div>

        {rows.map((row) => (
          <div
            key={row.key}
            className="grid min-h-[92px] border-b border-gray-100"
            style={{ gridTemplateColumns: `104px repeat(${columns.length}, minmax(200px, 1fr))` }}
          >
            <div className="sticky left-0 z-10 border-r border-gray-200 bg-white px-3 py-3">
              {view !== 'day' && <p className="text-[11px] font-semibold uppercase text-gray-500">{shortDate(row.date)}</p>}
              <p className="text-sm font-semibold text-gray-900">{row.time}</p>
            </div>
            {columns.map((professional) => {
              const cellAppointments = appointmentsByCell.get(`${professional.id}|${row.key}`) || [];

              return (
                <div key={`${professional.id}-${row.key}`} className="border-r border-gray-100 bg-white p-2">
                  {cellAppointments.length > 0 ? (
                    cellAppointments.map((appointment) => (
                      <AppointmentCard key={appointment.id || appointment.uuid} appointment={appointment} onEdit={onEdit} />
                    ))
                  ) : (
                    <button
                      type="button"
                      onClick={() => onCreate({
                        professional_id: professional.id === 'unassigned' ? '' : professional.id,
                        starts_at: `${row.date}T${row.time}`,
                        branch_id: professional.branch_id || ''
                      })}
                      className="h-full min-h-[56px] w-full rounded-md border border-dashed border-gray-200 text-xs font-semibold text-gray-400 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                      title="Crear cita en este horario"
                    >
                      Libre
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const Agenda = () => {
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pets, setPets] = useState([]);
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [branches, setBranches] = useState([]);
  const [view, setView] = useState('day');
  const [displayMode, setDisplayMode] = useState('calendar');
  const [date, setDate] = useState(isoDate(new Date()));
  const [status, setStatus] = useState('');
  const [selectedProfessionalIds, setSelectedProfessionalIds] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirm, setConfirm] = useState(null);
  const initializedProfessionalSelection = useRef(false);

  const customerById = useMemo(() => new Map(customers.map((item) => [Number(item.id), item])), [customers]);
  const petById = useMemo(() => new Map(pets.map((item) => [Number(item.id), item])), [pets]);
  const serviceById = useMemo(() => new Map(services.map((item) => [Number(item.id), item])), [services]);
  const professionalById = useMemo(() => new Map(professionals.map((item) => [Number(item.id), item])), [professionals]);
  const branchById = useMemo(() => new Map(branches.map((item) => [Number(item.id), item])), [branches]);
  const showBranchFilter = branches.length > 1;
  const calendarProfessionals = useMemo(() => {
    return professionals.filter((professional) => (
      professional.role === 'veterinarian' &&
      Number(professional.is_active ?? professional.active ?? 1) !== 0
    ));
  }, [professionals]);

  const loadAgenda = useCallback(async () => {
    try {
      setLoading(true);
      const range = rangeForView(date, view);
      const params = {
        ...range,
        status: status || undefined,
        branch_id: showBranchFilter ? branchId || undefined : undefined,
        limit: 100
      };
      const [appointmentData, customerData, petData, serviceData, professionalData, branchData] = await Promise.all([
        listAppointments(params),
        listCustomers({ limit: 100 }),
        listPets({ limit: 100 }),
        listServices({ limit: 100 }),
        listProfessionals({ include_inactive: 1 }),
        getBranches()
      ]);
      setAppointments(appointmentData);
      setCustomers(customerData);
      setPets(petData);
      setServices(serviceData);
      setProfessionals(professionalData);
      setBranches(branchData);

      const defaultProfessionalIds = professionalData
        .filter((professional) => professional.role === 'veterinarian' && Number(professional.is_active ?? professional.active ?? 1) !== 0)
        .map((professional) => String(professional.id));

      if (!initializedProfessionalSelection.current) {
        setSelectedProfessionalIds(defaultProfessionalIds);
        initializedProfessionalSelection.current = true;
      } else {
        setSelectedProfessionalIds((current) => current.filter((id) => defaultProfessionalIds.includes(String(id))));
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar la agenda'));
    } finally {
      setLoading(false);
    }
  }, [branchId, date, showBranchFilter, status, view]);

  useEffect(() => {
    loadAgenda();
  }, [loadAgenda]);

  const petsForCustomer = form.customer_id
    ? pets.filter((pet) => Number(pet.customer_id) === Number(form.customer_id))
    : pets;

  const visibleAppointments = useMemo(() => {
    if (selectedProfessionalIds.length === calendarProfessionals.length) {
      return appointments;
    }

    const selected = new Set(selectedProfessionalIds.map(String));
    return appointments.filter((appointment) => selected.has(String(appointment.professional_id)));
  }, [appointments, calendarProfessionals.length, selectedProfessionalIds]);

  const selectedProfessionals = useMemo(() => {
    const selected = new Set(selectedProfessionalIds.map(String));
    return calendarProfessionals.filter((professional) => selected.has(String(professional.id)));
  }, [calendarProfessionals, selectedProfessionalIds]);

  const calendarColumns = useMemo(() => {
    const hasUnassignedAppointments = visibleAppointments.some((appointment) => !appointment.professional_id);
    const selectedColumns = selectedProfessionals.map((professional) => ({
      ...professional,
      branch_id: professional.branch_id || professional.branch_ids?.[0] || branches[0]?.id || ''
    }));

    if (!hasUnassignedAppointments || selectedProfessionalIds.length !== calendarProfessionals.length) {
      return selectedColumns;
    }

    return [
      ...selectedColumns,
      {
        id: 'unassigned',
        full_name: 'Sin asignar',
        specialty: 'Citas pendientes de veterinario',
        branch_id: branches[0]?.id || ''
      }
    ];
  }, [branches, calendarProfessionals.length, selectedProfessionalIds.length, selectedProfessionals, visibleAppointments]);

  const toggleProfessional = (professionalId) => {
    const value = String(professionalId);
    setSelectedProfessionalIds((current) => (
      current.includes(value)
        ? current.filter((id) => id !== value)
        : [...current, value]
    ));
  };

  const selectAllProfessionals = () => {
    setSelectedProfessionalIds(calendarProfessionals.map((professional) => String(professional.id)));
  };

  const clearProfessionals = () => {
    setSelectedProfessionalIds([]);
  };

  const openCreate = (prefill = {}) => {
    setEditing(null);
    setForm({
      ...emptyForm,
      professional_id: prefill.professional_id || (selectedProfessionalIds.length === 1 ? selectedProfessionalIds[0] : ''),
      branch_id: prefill.branch_id || branchId || branches[0]?.id || '',
      starts_at: prefill.starts_at || `${date}T09:00`
    });
    setModalOpen(true);
  };

  const openEdit = (appointment) => {
    setEditing(appointment);
    setForm({
      customer_id: appointment.customer_id || '',
      pet_id: appointment.pet_id || '',
      service_id: appointment.service_id || '',
      professional_id: appointment.professional_id || '',
      branch_id: appointment.branch_id || branchId || branches[0]?.id || '',
      starts_at: toInputDateTime(appointment.starts_at),
      status: appointment.status || 'scheduled',
      notes: appointment.notes || ''
    });
    setModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const payload = {
        customer_id: Number(form.customer_id),
        pet_id: Number(form.pet_id),
        service_id: Number(form.service_id),
        professional_id: form.professional_id ? Number(form.professional_id) : null,
        branch_id: form.branch_id ? Number(form.branch_id) : branches[0]?.id || null,
        starts_at: fromInputDateTime(form.starts_at),
        status: form.status,
        notes: form.notes || null
      };

      if (editing) {
        await updateAppointment(editing.id, payload);
        toast.success('Cita actualizada');
      } else {
        await createAppointment(payload);
        toast.success('Cita creada');
      }

      setModalOpen(false);
      loadAgenda();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar la cita'));
    } finally {
      setSaving(false);
    }
  };

  const performAction = async () => {
    if (!confirm) return;

    try {
      setSaving(true);
      if (confirm.type === 'cancel') {
        await cancelAppointment(confirm.item.id, { reason: 'Cancelada desde admin' });
        toast.success('Cita cancelada');
      }
      if (confirm.type === 'complete') {
        await markAppointmentCompleted(confirm.item.id);
        toast.success('Cita marcada como atendida');
      }
      setConfirm(null);
      loadAgenda();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo actualizar la cita'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'starts_at',
      label: 'Fecha y hora',
      render: (row) => <span className="font-semibold text-gray-900">{dateTime(row.starts_at)}</span>
    },
    { key: 'customer_id', label: 'Cliente', render: (row) => row.customer_name || customerById.get(Number(row.customer_id))?.full_name || `#${row.customer_id}` },
    { key: 'pet_id', label: 'Mascota', render: (row) => row.pet_name || petById.get(Number(row.pet_id))?.name || `#${row.pet_id}` },
    { key: 'service_id', label: 'Servicio', render: (row) => row.service_name || serviceById.get(Number(row.service_id))?.name || `#${row.service_id}` },
    { key: 'professional_id', label: 'Veterinario', render: (row) => row.professional_name || professionalById.get(Number(row.professional_id))?.full_name || '-' },
    ...(showBranchFilter ? [{
      key: 'branch_id',
      label: 'Sucursal',
      render: (row) => row.branch_name || branchById.get(Number(row.branch_id))?.name || '-'
    }] : []),
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const config = getStatusConfig(appointmentStatus, row.status);
        return <StatusBadge label={config.label} color={config.color} />;
      }
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => openEdit(row)} className="rounded-md p-2 text-blue-700 hover:bg-blue-50" title="Editar o reagendar">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={() => setConfirm({ type: 'complete', item: row })} className="rounded-md p-2 text-green-700 hover:bg-green-50" title="Marcar atendida">
            <CheckCircle2 className="h-4 w-4" />
          </button>
          <button onClick={() => setConfirm({ type: 'cancel', item: row })} className="rounded-md p-2 text-red-700 hover:bg-red-50" title="Cancelar">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agenda"
        description="Crea, reprograma, cancela y marca citas como atendidas."
        actions={<Button icon={CalendarPlus} onClick={() => openCreate()}>Nueva cita</Button>}
      />

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className={`grid gap-3 ${showBranchFilter ? 'md:grid-cols-6' : 'md:grid-cols-5'}`}>
          <FormField label="Vista">
            <div className="grid grid-cols-3 rounded-md border border-gray-300 p-1">
              {viewOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setView(option.value)}
                  className={`rounded px-3 py-1.5 text-sm font-semibold ${view === option.value ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </FormField>
          <FormField label="Modo">
            <div className="grid grid-cols-2 rounded-md border border-gray-300 p-1">
              {displayOptions.map((option) => {
                const Icon = option.icon;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setDisplayMode(option.value)}
                    className={`inline-flex items-center justify-center gap-2 rounded px-3 py-1.5 text-sm font-semibold ${displayMode === option.value ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <Icon className="h-4 w-4" />
                    {option.label}
                  </button>
                );
              })}
            </div>
          </FormField>
          <FormField label="Fecha">
            <input type="date" className={inputClassName} value={date} onChange={(event) => setDate(event.target.value)} />
          </FormField>
          <FormField label="Estado">
            <select className={inputClassName} value={status} onChange={(event) => setStatus(event.target.value)}>
              {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </FormField>
          {showBranchFilter && (
            <FormField label="Sucursal">
              <select className={inputClassName} value={branchId} onChange={(event) => setBranchId(event.target.value)}>
                <option value="">Todas</option>
                {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
              </select>
            </FormField>
          )}
          <div className="flex items-end">
            <Button icon={Search} variant="secondary" onClick={loadAgenda} className="w-full">Actualizar</Button>
          </div>
        </div>

        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <Users className="h-4 w-4 text-blue-600" />
              Veterinarios visibles
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                {selectedProfessionalIds.length}/{calendarProfessionals.length}
              </span>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={selectAllProfessionals}>Todos</Button>
              <Button type="button" variant="ghost" size="sm" onClick={clearProfessionals}>Limpiar</Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {calendarProfessionals.map((professional) => {
              const selected = selectedProfessionalIds.includes(String(professional.id));

              return (
                <button
                  key={professional.id}
                  type="button"
                  onClick={() => toggleProfessional(professional.id)}
                  className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition ${selected ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                  {professional.full_name}
                </button>
              );
            })}
            {calendarProfessionals.length === 0 && (
              <p className="text-sm text-gray-500">No hay veterinarios activos para mostrar.</p>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? (
          <div className="p-5"><LoadingState rows={6} /></div>
        ) : displayMode === 'calendar' ? (
          <CalendarBoard
            appointments={visibleAppointments}
            columns={calendarColumns}
            dateValue={date}
            view={view}
            onCreate={openCreate}
            onEdit={openEdit}
          />
        ) : (
          <Table columns={columns} data={visibleAppointments} emptyMessage="No hay citas para el rango seleccionado" />
        )}
      </section>

      <Modal
        open={modalOpen}
        title={editing ? 'Editar o reagendar cita' : 'Nueva cita'}
        onClose={() => setModalOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
            <Button type="submit" form="appointment-form" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </div>
        }
      >
        <form id="appointment-form" onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <FormField label="Cliente" required>
            <select className={inputClassName} value={form.customer_id} onChange={(event) => setForm({ ...form, customer_id: event.target.value, pet_id: '' })} required>
              <option value="">Seleccionar cliente</option>
              {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.full_name}</option>)}
            </select>
          </FormField>
          <FormField label="Mascota" required>
            <select className={inputClassName} value={form.pet_id} onChange={(event) => setForm({ ...form, pet_id: event.target.value })} required>
              <option value="">Seleccionar mascota</option>
              {petsForCustomer.map((pet) => <option key={pet.id} value={pet.id}>{pet.name}</option>)}
            </select>
          </FormField>
          <FormField label="Servicio" required>
            <select className={inputClassName} value={form.service_id} onChange={(event) => setForm({ ...form, service_id: event.target.value })} required>
              <option value="">Seleccionar servicio</option>
              {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
            </select>
          </FormField>
          <FormField label="Veterinario">
            <select className={inputClassName} value={form.professional_id} onChange={(event) => setForm({ ...form, professional_id: event.target.value })}>
              <option value="">Sin asignar</option>
              {professionals.map((professional) => <option key={professional.id} value={professional.id}>{professional.full_name}</option>)}
            </select>
          </FormField>
          {showBranchFilter && (
            <FormField label="Sucursal">
              <select className={inputClassName} value={form.branch_id} onChange={(event) => setForm({ ...form, branch_id: event.target.value })}>
                {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
              </select>
            </FormField>
          )}
          <FormField label="Fecha y hora" required>
            <input type="datetime-local" className={inputClassName} value={form.starts_at} onChange={(event) => setForm({ ...form, starts_at: event.target.value })} required />
          </FormField>
          <FormField label="Estado">
            <select className={inputClassName} value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
              {statusOptions.filter((option) => option.value).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Notas">
              <textarea rows={3} className={inputClassName} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
            </FormField>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.type === 'cancel' ? 'Cancelar cita' : 'Marcar como atendida'}
        message={confirm?.type === 'cancel' ? 'La cita quedara cancelada.' : 'La cita quedara registrada como atendida.'}
        confirmLabel={confirm?.type === 'cancel' ? 'Cancelar cita' : 'Marcar atendida'}
        loading={saving}
        onCancel={() => setConfirm(null)}
        onConfirm={performAction}
      />
    </div>
  );
};

export default Agenda;
